// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useCallback, useEffect, useRef, useState } from 'react'
import { RadioChoice } from '../../components/ui/FormFields'
import { PREGNANCY_OPTIONS, pregnancySeed, type PregnancyStatus } from './pregnancyStatus'

/**
 * The radio's state for the enrollment door. Seeds arrive asynchronously (the enrollment-draft read
 * on edit), so a ref — not the state — guards the operator's own choice from a late seed. `payload`
 * is the value to SEND: only a choice the operator made, so a plain re-save of the form never
 * rewrites a pharmacist's later screening answer.
 *
 * `resetKey` (create mode: the selected patient's id): when it changes, the choice and its touched
 * flag are cleared, so a pick made for one patient is never sent for the next.
 */
export function usePregnancyRadio(resetKey?: string | null) {
  const [value, setValue] = useState<PregnancyStatus | ''>('')
  const touched = useRef(false)
  const lastKey = useRef(resetKey)
  useEffect(() => {
    if (lastKey.current === resetKey) return
    lastKey.current = resetKey
    touched.current = false
    setValue('')
  }, [resetKey])
  const pick = useCallback((v: PregnancyStatus) => { touched.current = true; setValue(v) }, [])
  const seed = useCallback((univ09: string | null | undefined, univ11: string | null | undefined) => {
    if (!touched.current) setValue(pregnancySeed(univ09, univ11))
  }, [])
  const payload = (): PregnancyStatus | undefined => (touched.current && value ? value : undefined)
  return { value, pick, seed, payload }
}

/**
 * D-CRX-86 (ASP point #1) — the enrollment form's Pregnancy / breastfeeding radio.
 *
 * Optional: blank means "not asked here" and writes nothing. The server turns a choice into the
 * screening answers the enrollment PDF prints (UNIV-09 pregnant?, UNIV-11 breastfeeding?), and
 * Pregnant / Unknown flag the enrollment for pharmacist review (UNIV-09's soft hold, which only
 * blocks Approve where the pharmacy has the screening gate on) — the note says so before Save.
 */
export default function PregnancyStatusField({ value, onChange, disabled }: {
  value: PregnancyStatus | ''; onChange: (v: PregnancyStatus) => void; disabled?: boolean
}) {
  const holds = value === 'pregnant' || value === 'unknown'
  return (
    <div data-testid="pregnancy-status" style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12,
      padding: '8px 10px', borderRadius: 8, background: 'var(--color-surface-2, #f9fafb)', border: '0.5px solid var(--color-border, #e5e7eb)' }}>
      <RadioChoice label="Pregnancy / breastfeeding (patient-reported)" value={value} onChange={onChange}
        options={PREGNANCY_OPTIONS} disabled={disabled} />
      <span style={{ fontSize: 10.5, color: 'var(--color-text-mute, #9ca3af)' }}>
        {value === ''
          ? 'Optional · saved with this enrollment and printed on the enrollment form.'
          : holds
            ? 'Pregnant / Unknown flags this enrollment for pharmacist review.'
            : 'Saved with this enrollment and printed on the enrollment form.'}
      </span>
    </div>
  )
}
